import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import type { DestinationId, DestinationRunStatus } from '../../lib/types';
import { DESTINATIONS } from '../../lib/destinations';
import { StatusBadge } from '../ui/StatusBadge';
import { cn, formatNumber } from '../../lib/utils';
interface Props {
  selected: DestinationId[];
  totalRecords: number;
  onComplete: (results: Record<DestinationId, DestinationRunStatus>) => void;
}
// Deterministic end-states for the demo: Five9 fails, Bonzo warns.
const FINAL: Record<DestinationId, DestinationRunStatus> = {
  salesforce: 'completed',
  bonzo: 'completed-with-warnings',
  five9: 'failed'
};
export function ProcessingView({
  selected,
  totalRecords,
  onComplete
}: Props) {
  const [progress, setProgress] = useState(0);
  const [statuses, setStatuses] = useState<Record<string, DestinationRunStatus>>(() => Object.fromEntries(selected.map(id => [id, 'waiting'])));
  useEffect(() => {
    let pct = 0;
    const order = selected;
    let idx = 0;
    // kick off first destination
    setStatuses(s => ({
      ...s,
      [order[0]]: 'processing'
    }));
    const timer = setInterval(() => {
      pct = Math.min(100, pct + 2.5);
      setProgress(pct);
      const boundary = (idx + 1) / order.length * 100;
      if (pct >= boundary && idx < order.length) {
        const finished = order[idx];
        setStatuses(s => ({
          ...s,
          [finished]: FINAL[finished]
        }));
        idx += 1;
        if (idx < order.length) {
          const next = order[idx];
          setStatuses(s => ({
            ...s,
            [next]: 'processing'
          }));
        }
      }
      if (pct >= 100) {
        clearInterval(timer);
        const results = Object.fromEntries(order.map(id => [id, FINAL[id]])) as Record<DestinationId, DestinationRunStatus>;
        setStatuses(results);
        setTimeout(() => onComplete(results), 700);
      }
    }, 130);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const processed = Math.round(progress / 100 * totalRecords);
  const remaining = totalRecords - processed;
  const current = selected.find(id => statuses[id] === 'processing');
  const currentDef = DESTINATIONS.find(d => d.id === current);
  return <div className="mx-auto max-w-3xl space-y-6" data-id="element-357" data-mp="208pmtyn0qgrp">
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-card" data-id="element-358" data-mp="2nxiirv3n4omb">
        <div className="mb-2 flex items-end justify-between" data-id="element-359" data-mp="2nzv0c75hkwyu">
          <div data-id="element-360" data-mp="2svae6z3husv9">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-361" data-mp="2svtte81rf35l">
              Uploading your leads…
            </h3>
            <p className="text-sm text-gray-500" data-id="element-362" data-mp="2f3dxrsb6u7ep">
              {currentDef ? `Currently processing: ${currentDef.name}` : 'Finalizing…'}
            </p>
          </div>
          <span className="text-2xl font-bold text-salesforce-600" data-id="element-363" data-mp="36ujqvoshr4uh">
            {Math.round(progress)}%
          </span>
        </div>
        <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-gray-100" data-id="element-364" data-mp="107ce6hsaebkv">
          <motion.div className="h-full rounded-full bg-salesforce-500" animate={{
          width: `${progress}%`
        }} transition={{
          ease: 'linear'
        }} data-id="element-365" data-mp="v9aym6u2kue5" />
        </div>
        <div className="mt-3 flex justify-between text-xs text-gray-500" data-id="element-366" data-mp="14zmacfjzw7vl">
          <span data-id="element-367" data-mp="ha1fayakhk5y">{formatNumber(processed)} processed</span>
          <span data-id="element-368" data-mp="ca6rx4q03cpn">{formatNumber(remaining)} remaining</span>
        </div>
      </div>

      <div className="space-y-3" data-id="element-369" data-mp="ccjexwam8d9k">
        {selected.map(id => {
        const def = DESTINATIONS.find(d => d.id === id)!;
        const st = statuses[id];
        return <div key={id} className={cn('flex items-center gap-4 rounded-2xl border bg-white p-4 shadow-card transition-colors', st === 'processing' ? 'border-salesforce-300 ring-1 ring-salesforce-200' : 'border-gray-200')} data-id="element-370" data-mp="m71ohi2pr31a">
              <img src={def.icon} alt="" className="h-10 w-10 rounded-lg" data-id="element-371" data-mp="c4pj2xecqzd2" />
              <div className="min-w-0 flex-1" data-id="element-372" data-mp="g72qrrcnt7ch">
                <p className="text-sm font-semibold text-gray-900" data-id="element-373" data-mp="g6azutu0bxsj">
                  {def.name}
                </p>
                <p className="text-xs text-gray-500" data-id="element-374" data-mp="l7gy176fsd9g">
                  {id === 'salesforce' && 'Creating Salesforce leads'}
                  {id === 'bonzo' && 'Sending prospects to Bonzo'}
                  {id === 'five9' && 'Adding records to Five9'}
                </p>
              </div>
              <StatusBadge status={st} size="sm" data-id="element-375" data-mp="zvv925e0b813" />
            </div>;
      })}
      </div>

      <p className="text-center text-xs text-gray-400" data-id="element-376" data-mp="13vvmpnta7f73">
        You can keep this tab open — large uploads process in the background and
        each destination completes independently.
      </p>
    </div>;
}